/**
 * The one-field dialog behind "create folder", "create workspace" and every rename
 * (spec M1-B1 §7.1, §7.5). One component rather than five near-identical ones: the only thing
 * that differs between them is the title, the label and what the name is handed to.
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useState } from 'react';

export function NameDialog({
  title,
  label,
  initial = '',
  confirmLabel = 'Create',
  testId,
  onSubmit,
  onCancel,
}: {
  title: string;
  label: string;
  initial?: string;
  confirmLabel?: string;
  testId: string;
  onSubmit(name: string): void;
  onCancel(): void;
}) {
  const [name, setName] = useState(initial);
  const trimmed = name.trim();
  /**
   * **P-260.** Escape used to be a `keydown` on the input alone — it closed the dialog and left
   * focus on `<body>`, because the input it was typed into went with the dialog. The hook owns
   * both halves now, and because it is the **hoisted** pattern (the workspace `···` menu closes
   * in the commit that opens this) it inherits the menu's trigger rather than finding nothing.
   */
  useOverlayDismiss({ onClose: onCancel });

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onCancel}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed === '') return;
          onSubmit(trimmed);
        }}
      >
        <h2 className="ez-board-dialog__title">{title}</h2>
        <label className="ez-board-dialog__field">
          {label}
          <input
            data-testid={`${testId}-name`}
            value={name}
            maxLength={255}
            // The dialog exists to be typed into.
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="ez-board-dialog__save"
            data-testid={`${testId}-submit`}
            disabled={trimmed === ''}
          >
            {confirmLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}
